import React, { useState, useEffect } from 'react';
import { DollarSign, Hash, Target, Scale, TriangleAlert, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../../services/api';
import EquityChart from '../../components/EquityChart';
import KPITile from '../../components/KPITile';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const PROFIT = '#22C55E';
const LOSS   = '#EF4444';
const AXIS   = '#93A4B3';
const GRID   = 'rgba(255,255,255,0.05)';

export default function CustomerPerformance() {
  const [perf, setPerf]     = useState(null);
  const [days, setDays]     = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/customer/performance?days=${days}`)
      .then(r => setPerf(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading performance data…</p>;
  if (!perf)   return <p style={{ color: 'var(--loss)' }}>Failed to load performance.</p>;

  const { snapshots, stats } = perf;
  const totalPnl = stats.totalPnl ?? 0;

  const winLossData = {
    labels: ['Wins', 'Losses'],
    datasets: [{ data: [stats.winTrades, stats.lossTrades], backgroundColor: [PROFIT, LOSS], borderWidth: 0 }],
  };
  const dailyPnlData = {
    labels: snapshots.map(s => new Date(s.date).toLocaleDateString()),
    datasets: [{
      label: 'Daily P&L',
      data: snapshots.map(s => s.dailyPnl),
      backgroundColor: snapshots.map(s => s.dailyPnl >= 0 ? PROFIT : LOSS),
    }],
  };

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Customer</span>
          <h1>Performance</h1>
          <div className="subtitle">How your EAs have traded over time</div>
        </div>
        <div className="btn-group">
          {[7, 30, 90, 180].map(d => (
            <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDays(d)}>{d}D</button>
          ))}
        </div>
      </div>

      <div className="row">
        <Tile><KPITile label="Gross P&L" value={`${totalPnl >= 0 ? '+' : '−'}$${Math.abs(totalPnl).toFixed(2)}`}
          sub="Realized, this period" icon={<DollarSign size={16} />}
          intent={totalPnl > 0 ? 'profit' : totalPnl < 0 ? 'loss' : 'neutral'}
          trend={totalPnl > 0 ? 'up' : totalPnl < 0 ? 'down' : 'flat'} /></Tile>
        <Tile><KPITile label="Total Trades" value={stats.totalTrades ?? 0} sub="Closed in period"
          icon={<Hash size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Win Rate" value={`${stats.winRate ?? 0}%`} sub={`${stats.winTrades ?? 0}W · ${stats.lossTrades ?? 0}L`}
          icon={<Target size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Profit Factor" value={stats.profitFactor ?? 0} sub="Gross profit ÷ loss"
          icon={<Scale size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Max Drawdown" value={`${stats.maxDrawdown ?? 0}%`} sub="Peak-to-trough"
          icon={<TriangleAlert size={16} />} intent="warning" /></Tile>
        <Tile><KPITile label="Avg Win" value={`+$${stats.avgWin ?? 0}`} sub="Per winning trade"
          icon={<TrendingUp size={16} />} intent="profit" /></Tile>
        <Tile><KPITile label="Avg Loss" value={`−$${stats.avgLoss ?? 0}`} sub="Per losing trade"
          icon={<TrendingDown size={16} />} intent="loss" /></Tile>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Equity Curve</h5>
              <div className="card-category" style={{ marginTop: 4 }}>Last {days} days</div>
            </div>
            <div className="card-body"><EquityChart snapshots={snapshots} height={280} /></div>
          </div>
        </div>

        <div className="col-lg-8 mb-4">
          <div className="card" style={{ height: '100%' }}>
            <div className="card-header"><h5 className="card-title">Daily P&L</h5></div>
            <div className="card-body" style={{ height: 300 }}>
              <Bar data={dailyPnlData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: AXIS }, grid: { display: false } },
                  y: { ticks: { color: AXIS }, grid: { color: GRID } },
                },
              }} />
            </div>
          </div>
        </div>
        <div className="col-lg-4 mb-4">
          <div className="card" style={{ height: '100%' }}>
            <div className="card-header"><h5 className="card-title">Win / Loss</h5></div>
            <div className="card-body d-flex justify-content-center align-items-center" style={{ height: 300 }}>
              {stats.totalTrades > 0 ? (
                <Doughnut data={winLossData} options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { labels: { color: AXIS } } },
                }} />
              ) : <p className="text-muted">No trade data</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Tile = ({ children }) => <div className="col-lg-3 col-md-4 col-6 mb-4">{children}</div>;
